/**
 * Actor earnings + Stripe Connect Express API surface.
 *
 * V1 vendor lock: Stripe Invoicing (platform → customer) + Stripe Connect
 * Express (customer → actor). OHEAR.AI never stores bank details. The
 * onboarding link returned here is a Stripe-hosted AccountLink — the user is
 * sent off-domain to complete KYC.
 *
 * 1099 download URLs are produced server-side from Stripe's reporting API.
 */
import { useMutation, useQuery } from '@tanstack/react-query'
import { api } from '../api'

export interface EarningsSummary {
  /** Calendar year being summarized. */
  year: number
  /** Cents already paid out for this year. */
  paid_cents: number
  /** Cents earned + approved but not yet paid. */
  outstanding_cents: number
  /** Cents from in-progress projects (estimate based on contract terms). */
  in_progress_estimate_cents: number
  currency: 'USD'
}

export interface EarningsBatchRow {
  batch_id: string
  batch_label: string
  status: 'in_progress' | 'awaiting_payout' | 'paid'
  amount_cents: number
  /** ISO-8601; null until payout posts. */
  paid_at: string | null
}

export interface EarningsProjectRow {
  project_id: string
  project_name: string
  organization_name: string
  /** Plain-language contract terms, e.g. "$300/batch, 3 batches". */
  contract_summary: string
  batches: readonly EarningsBatchRow[]
}

export interface EarningsForm1099 {
  year: number
  /** Signed Stripe URL or platform-hosted PDF; opens in new tab. */
  url: string
}

export interface EarningsPayload {
  summary: readonly EarningsSummary[]
  projects: readonly EarningsProjectRow[]
  forms_1099: readonly EarningsForm1099[]
}

export type StripeAccountStatus = 'not_started' | 'pending' | 'active' | 'restricted' | 'rejected'

export interface StripeConnectStatus {
  status: StripeAccountStatus
  /** Last four of the receiving account, if known. Always masked. */
  bank_last4: string | null
  /** True when Stripe will accept transfers to the account. */
  payouts_enabled: boolean
  /** True when Stripe accepts charges (orthogonal but Stripe surfaces both). */
  charges_enabled: boolean
  /** ISO-8601 of the last successful payout, if any. */
  last_payout_at: string | null
}

export interface OnboardingLink {
  /**
   * Single-use Stripe AccountLink. Expires within minutes — fetch fresh on
   * every "Manage in Stripe" click rather than caching.
   */
  url: string
  expires_at: string
}

const earningsKey = ['account', 'earnings'] as const
const stripeStatusKey = ['account', 'stripe-status'] as const

/** Per-year, per-project earnings + 1099 links. */
export function useEarnings() {
  return useQuery({
    queryKey: earningsKey,
    queryFn: () => api<EarningsPayload>('/api/v1/me/earnings'),
  })
}

/** Stripe Connect Express account status. */
export function useStripeConnectStatus() {
  return useQuery({
    queryKey: stripeStatusKey,
    queryFn: () => api<StripeConnectStatus>('/api/v1/me/stripe-status'),
  })
}

/**
 * Mint a fresh AccountLink. Returns a URL the caller should `window.open` or
 * `window.location.assign` to. Do not cache — Stripe links expire in minutes.
 */
export function useStripeOnboardingLink() {
  return useMutation({
    mutationFn: () => api<OnboardingLink>('/api/v1/me/stripe-onboarding-link', { method: 'POST' }),
  })
}
